import { useRef, useState } from "react"
import { useParams } from "react-router-dom"
import { FaArrowUp } from "react-icons/fa"
import { Box, HStack, IconButton } from "@chakra-ui/react"
import { Section, Text } from "@/components"
import { Button, IntegrityCheckButton } from "@/components/ui"
import { useAuth } from "@/contexts"
import { useCompanyInfo } from "@/hooks"
import { CompanyFlags, CompanyTabs } from "./components"
import { CompanyAssessmentPanel } from "./components/CompanyAssessmentPanel"
import { PortalRole } from "@/types"
import { useQueryClient } from "@tanstack/react-query"

const tierColor = (t: string) =>
  t === "A" ? "#22c55e" : t === "B" ? "#3b82f6" : t === "C" ? "#f59e0b" : "#ef4444"
const tierBg = (t: string) =>
  t === "A" ? "rgba(34,197,94,0.1)" : t === "B" ? "rgba(59,130,246,0.1)" : t === "C" ? "rgba(245,158,11,0.1)" : "rgba(239,68,68,0.1)"

export const CompanyComponent = () => {
  const params = useParams<{ companyId: string }>()
  const companyId = Number(params.companyId)

  const { user } = useAuth()
  const queryClient = useQueryClient()
  const { data: companyInfo, isLoading: isCompanyInfoLoading } = useCompanyInfo(companyId)

  const sectionRef = useRef<HTMLDivElement>(null)
  const [assessmentOpen, setAssessmentOpen] = useState(false)

  const scrollToTop = () => {
    sectionRef.current?.scrollTo({ top: 0, behavior: "smooth" })
  }

  const handleAssessmentComplete = () => {
    queryClient.invalidateQueries({ queryKey: ["companyInfo", companyId] })
  }

  if (!user) {
    return <div>Loading...</div>
  }

  if (user.userType !== PortalRole.ESO_PERSON) {
    console.log(user.userType)
    return <div>Please contact support to be assigned a deal manager.</div>
  }

  if (isCompanyInfoLoading) {
    return <div>Loading company info...</div>
  }

  if (!companyInfo) {
    return <div>No company info found.</div>
  }

  const assessment = companyInfo.assessment as {
    overallScore?: number
    tier?: string
    signal?: string
    capTableGateFailed?: boolean
    investmentOneLiner?: string | null
  } | null | undefined
  const isGreat = companyInfo.isGreat as boolean | undefined

  return (
    <Section ref={sectionRef} overflowY="auto" h="100vh">
      <Text fontSize="2xl" fontWeight="bold">
        {companyInfo.name}
      </Text>
      <Text fontSize="sm">
        {companyInfo.legalName}
      </Text>

      {/* Score Card button + Signs of Greatness badge */}
      <HStack gap={2} mt={2} flexWrap="wrap">
        <Box
          display="inline-flex"
          alignItems="center"
          px={4}
          py={1.5}
          borderRadius="full"
          cursor="pointer"
          onClick={() => setAssessmentOpen(true)}
          transition="all 0.15s"
          _hover={{ opacity: 0.8 }}
          {...(assessment?.tier
            ? {
                bg: tierBg(assessment.tier),
                border: `1.5px solid ${tierColor(assessment.tier)}`,
              }
            : {
                bg: "transparent",
                border: "1.5px solid",
                borderColor: "border.default",
              }
          )}
        >
          <Text
            fontSize="12px"
            fontWeight="600"
            letterSpacing="0.02em"
            color={assessment?.tier ? tierColor(assessment.tier) : "text.muted"}
          >
            {assessment?.overallScore != null
              ? `Tier ${assessment.tier} \u2014 ${assessment.overallScore}/100${assessment.capTableGateFailed ? " \u26A0" : ""}`
              : "Company Score Card"}
          </Text>
        </Box>
        {isGreat && (
          <Box
            display="inline-flex"
            alignItems="center"
            px={3}
            py={1}
            borderRadius="full"
            bg="rgba(234,179,8,0.12)"
            border="1.5px solid rgba(234,179,8,0.6)"
          >
            <Text fontSize="11px" color="#eab308" fontWeight="700" letterSpacing="0.05em">
              SIGNS OF GREATNESS
            </Text>
          </Box>
        )}
        {companyInfo.hotlist?.isActive && (
          <Box
            display="inline-flex"
            alignItems="center"
            px={3}
            py={1}
            borderRadius="full"
            bg="rgba(239,68,68,0.12)"
            border="1.5px solid rgba(239,68,68,0.6)"
          >
            <Text fontSize="11px" color="#ef4444" fontWeight="700" letterSpacing="0.05em">
              ON HOTLIST
            </Text>
          </Box>
        )}
        <IntegrityCheckButton type="company" companyId={companyId} />
      </HStack>

      {/* Investment one-liner */}
      {assessment?.investmentOneLiner && (
        <Text fontSize="12px" color="text.muted" fontStyle="italic" mt={1} maxW="700px" lineHeight="1.5">
          {assessment.investmentOneLiner}
        </Text>
      )}

      <CompanyTabs
        companyInfo={companyInfo}
        employeeCounts={companyInfo.employeeCounts || []}
      />

      <HStack position="fixed" bottom={6} right={6} gap={3}>
        <CompanyFlags companyInfo={companyInfo} />
        <IconButton
          aria-label="Scroll to top"
          size="lg"
          borderRadius={10}
          boxShadow="lg"
          bg="fab.bg"
          onClick={scrollToTop}
        >
          <FaArrowUp color="fab.icon" />
        </IconButton>
      </HStack>

      <CompanyAssessmentPanel
        companyId={companyId}
        companyName={companyInfo.name}
        isOpen={assessmentOpen}
        onClose={() => setAssessmentOpen(false)}
        onAssessmentComplete={handleAssessmentComplete}
      />
    </Section>
  )
}
